import { PromotionClient, type UseQueryOverrideOptions } from '@core/types'
import type { DepositOffer } from '@payments/types'
import { useState, useNuxtApp, useNuxtData, computed, isAbortError, useQuery, useMutation, useUserInfo, usePromotionClient, toValue } from '#imports'

const PROMOTION_KEY = 'depositPromotion:pending-offers-with-payment-requirements'

export const useDepositPromotion = () => {
    const nuxtApp = useNuxtApp()
    const { canUsePlayerFeatures, status: userInfoStatus } = useUserInfo()
    const { client: promotionClient } = usePromotionClient()

    const { data: promotion } = useNuxtData<DepositOffer | null>(PROMOTION_KEY)
    const isPromotionToggled = useState<boolean>('promotion:is-toggled', () => false)

    const hasPromotion = computed(() => !!promotion.value)

    const promotionBonus = computed(() => promotion.value?.bonusType === PromotionClient.BonusType.CASH ? promotion.value : undefined)

    const displayedPromotionBonus = computed(() => {
        if (
            promotion.value?.signup?.toggleable
            && !isPromotionToggled.value
        ) return

        return promotionBonus.value
    })

    const hasDepositPromotion = computed(() => !!promotion.value?.signup)

    const isPromotionToggleable = computed(() => promotion.value?.signup?.toggleable ?? false)

    const promotionBonusPercentage = computed(() => Number(promotionBonus.value?.percentage))

    const promotionBonusMaxAmount = computed(() => Number(promotionBonus.value?.percentageAmountLimit) || Number(promotionBonus.value?.amount))

    const promotionBonusMinAmount = computed(() => Number(promotionBonus.value?.minPaymentAmount))

    const isActivePromotion = computed(() => {
        if (!hasDepositPromotion.value) return false

        if (isPromotionToggleable.value) return isPromotionToggled.value

        return true
    })

    function isAmountHigherThanBonusAmount(amountValue: number) {
        return promotionBonusMaxAmount.value < amountValue
    }

    function isAmountWithinBonusRequirement(amountValue: number) {
        return amountValue >= promotionBonusMinAmount.value
    }

    function bonusAmount(amountValue: number) {
        if (amountValue < promotionBonusMinAmount.value) return 0

        if (promotionBonusPercentage.value) {
            const isBonusAmountEqualToOrHigherThanLimit = promotionBonusPercentage.value / 100 * amountValue >= promotionBonusMaxAmount.value

            if (isBonusAmountEqualToOrHigherThanLimit) return promotionBonusMaxAmount.value

            return (promotionBonusPercentage.value / 100) * amountValue
        }

        return promotionBonusMaxAmount.value
    }

    function totalBonusAmount(amountValue: number) {
        const numberedAmount = amountValue

        if (!displayedPromotionBonus.value) return numberedAmount

        const amountForBonus = bonusAmount(amountValue)

        if (amountForBonus >= promotionBonusMaxAmount.value) return numberedAmount + promotionBonusMaxAmount.value
        if (numberedAmount < promotionBonusMinAmount.value) return numberedAmount

        return numberedAmount + amountForBonus
    }

    function useGetDepositPromotions(options: UseQueryOverrideOptions = {}) {
        const { enabled, ...restOptions } = options

        return useQuery<DepositOffer | null>(async ({ signal }) => {
            try {
                const { data } = await promotionClient.GET('/v1/public/player-offer-requirements/pending-payment', { signal })

                return data ?? null
            }
            catch (error) {
                if (isAbortError(error)) throw error

                // useOpenapi has already reported it; a PS outage must not block deposits.
                return null
            }
        }, {
            lazy: true,
            server: false,
            ...restOptions,
            key: PROMOTION_KEY,
            enabled: () => {
                if (!canUsePlayerFeatures.value || userInfoStatus.value === 'pending') return false

                return toValue(options.enabled ?? true)
            }
        })
    }

    const activatedOfferIds = useState<string[]>('promotion:activated-offer-ids', () => [])

    const activatePromotionMutation = useMutation(async () => {
        if (!isPromotionToggleable.value) return

        const playerOfferId = promotion.value?.signup?.playerOfferId

        if (!playerOfferId || activatedOfferIds.value.includes(playerOfferId)) return

        const { data } = await promotionClient.PUT('/public/player-offers/{id}/activate', {
            params: {
                path: {
                    id: playerOfferId
                }
            }
        })

        activatedOfferIds.value.push(playerOfferId)

        return data
    }, {
        mode: 'leading',
        onSuccess: () => {
            if (!promotion.value?.signup) return

            promotion.value = { ...promotion.value, signup: { ...promotion.value.signup, toggleable: false } }
            // The query cache reads the payload, which the assignment above leaves on the old offer.
            nuxtApp.payload.data[PROMOTION_KEY] = promotion.value
        }
    })

    return {
        promotion,
        isPromotionToggled,
        hasPromotion,
        promotionBonus,
        displayedPromotionBonus,
        hasDepositPromotion,
        isPromotionToggleable,
        promotionBonusPercentage,
        promotionBonusMaxAmount,
        promotionBonusMinAmount,
        isActivePromotion,
        isAmountHigherThanBonusAmount,
        isAmountWithinBonusRequirement,
        bonusAmount,
        totalBonusAmount,
        useGetDepositPromotions,
        activatePromotionMutation,
    }
}
